import axios, { isAxiosError } from 'axios';
import type {
  IPublicItemDetail,
  IPublicItemSummary,
  IPublicPage,
  IPublicWorkArea,
} from '@featit/shared';
import { API_BASE_URL } from './ApiService';

/**
 * The signed-out read surface (F-0338, concept §10.7a): `GET /api/public/…`.
 *
 * ⛔ **No credential, ever.** This is plain `axios`, not `ApiService`: no `Authorization` header, no
 * refresh, no sign-in redirect on a 401, no cookie (`withCredentials: false`). A signed-in person
 * reading a Public work area they are not a member of is answered exactly as a stranger (AC-PV18), and
 * a client that attached their token would only be handing it to a surface that ignores it. The
 * spec (`PublicReadApi.test.ts`) fails on any header it did not mean to send.
 *
 * Every refusal is one thing here — `not-found`, which is what a private, missing, taken-down and
 * not-yet-public work area all are (AC-PV6) — except being asked too often, which says how long to wait.
 */
export type PublicReadFailureType =
  | { kind: 'not-found' }
  | { kind: 'limited'; retryAfterSeconds: number }
  | { kind: 'failed' };

export class PublicReadError extends Error {
  constructor(readonly failure: PublicReadFailureType) {
    super(
      failure.kind === 'not-found'
        ? 'Not found'
        : failure.kind === 'limited'
          ? `Too many requests; retry in ${failure.retryAfterSeconds} s`
          : 'The public read failed',
    );
  }
}

const TIMEOUT_MS = 15_000;

function failureOf(error: unknown): PublicReadError {
  if (isAxiosError(error)) {
    if (error.response?.status === 404) return new PublicReadError({ kind: 'not-found' });
    if (error.response?.status === 429) {
      const wait = Number(error.response.headers?.['retry-after']);
      return new PublicReadError({
        kind: 'limited',
        retryAfterSeconds: Number.isFinite(wait) && wait > 0 ? wait : 30,
      });
    }
  }
  return new PublicReadError({ kind: 'failed' });
}

async function get<T>(
  path: string,
  params?: Record<string, string | number | undefined>,
): Promise<T> {
  try {
    const response = await axios.get<T>(`${API_BASE_URL}/public/${path}`, {
      params,
      timeout: TIMEOUT_MS,
      withCredentials: false,
      // Nothing from the app's own axios defaults (a token interceptor lives on the app's client,
      // not on this module's use of the global one, but a header default could be set anywhere).
      headers: { Authorization: undefined },
    });
    return response.data;
  } catch (error) {
    throw failureOf(error);
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * An answer is drawn only when it is the shape the view reads. Anything else — an empty body, a list,
 * a proxy's page, a stand-in that answers every path alike — is `failed`, which every caller already
 * turns into what the address showed before (gate 470g: a 200 `[]` was taken for a work area and an
 * item, and the item view crashed on `item.type.name` instead of saying there was nothing there).
 */
function isPublicWorkArea(value: unknown): value is IPublicWorkArea {
  return (
    isRecord(value) &&
    typeof value.id === 'string' &&
    typeof value.name === 'string' &&
    typeof value.path === 'string'
  );
}

function isPublicItemDetail(value: unknown): value is IPublicItemDetail {
  return (
    isRecord(value) &&
    typeof value.key === 'string' &&
    typeof value.title === 'string' &&
    isRecord(value.type) &&
    typeof value.type.name === 'string' &&
    (value.status === null || (isRecord(value.status) && typeof value.status.name === 'string')) &&
    Array.isArray(value.comments) &&
    Array.isArray(value.attachments)
  );
}

function isPublicPage(value: unknown): value is IPublicPage<IPublicItemSummary> {
  return isRecord(value) && Array.isArray(value.items);
}

async function getShaped<T>(
  path: string,
  shaped: (value: unknown) => value is T,
  params?: Record<string, string | number | undefined>,
): Promise<T> {
  const answer = await get<unknown>(path, params);
  if (!shaped(answer)) throw new PublicReadError({ kind: 'failed' });
  return answer;
}

export const publicReadApi = {
  /** A Public work area by its address (`acme/tools/tracker`). */
  workArea: (address: string): Promise<IPublicWorkArea> =>
    getShaped('work-area', isPublicWorkArea, { path: address }),

  items: (
    workAreaId: string,
    options: { cursor?: string | null; limit?: number } = {},
  ): Promise<IPublicPage<IPublicItemSummary>> =>
    getShaped(`work-areas/${encodeURIComponent(workAreaId)}/items`, isPublicPage, {
      cursor: options.cursor ?? undefined,
      limit: options.limit,
    }),

  search: (
    workAreaId: string,
    query: string,
    options: { cursor?: string | null } = {},
  ): Promise<IPublicPage<IPublicItemSummary>> =>
    getShaped(`work-areas/${encodeURIComponent(workAreaId)}/search`, isPublicPage, {
      q: query,
      cursor: options.cursor ?? undefined,
    }),

  item: (workAreaId: string, key: string): Promise<IPublicItemDetail> =>
    getShaped(
      `work-areas/${encodeURIComponent(workAreaId)}/items/${encodeURIComponent(key)}`,
      isPublicItemDetail,
    ),

  /** Where an attachment of a published item downloads from: the API answers with a short-lived redirect. */
  attachmentHref: (workAreaId: string, key: string, attachmentId: string): string =>
    `${API_BASE_URL}/public/work-areas/${encodeURIComponent(workAreaId)}/items/${encodeURIComponent(key)}/attachments/${encodeURIComponent(attachmentId)}`,
};
